import React, { useEffect, useState } from "react";
import { BASE_URL } from "../../../config/api";
import { ChevronDown, ChevronUp } from "lucide-react";

function RetailerTransactions() {

  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  const authUser = JSON.parse(localStorage.getItem("authUserData"));
  const companyId = authUser?.company_id;
  const retailerId = authUser?.id;
  const role = Number(authUser?.role);

  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isRetailerMobile = role === 5 && isMobile;

  const fetchTransactions = async () => {
    try {

      const res = await fetch(
        `${BASE_URL}company/retailer-transactions/${companyId}/${retailerId}`
      );

      const data = await res.json();

      if (data?.success) {
        setTransactions(data.transactions || []);
      }

    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTransactions();
  }, []);

  return (
    <div className="p-4">

      <h2 className="text-xl font-semibold text-gray-800 mb-4">
        Wallet Transactions
      </h2>

      {loading ? (

        <div className="space-y-3">
          {[1,2,3,4,5].map((i) => (
            <TransactionSkeleton
              key={i}
              isRetailerMobile={isRetailerMobile}
            />
          ))}
        </div>

      ) : transactions.length === 0 ? (
        <p>No transactions found</p>
      ) : (

        <div className="space-y-3">

          {transactions.map((tx) => (
            <TransactionRow
              key={tx.payment_id}
              tx={tx}
              companyId={companyId}
              isRetailerMobile={isRetailerMobile}
            />
          ))}

        </div>

      )}

    </div>
  );
}

function TransactionRow({ tx, companyId, isRetailerMobile }) {

  const [open, setOpen] = useState(false);
  const [invoices, setInvoices] = useState([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);

  const fetchInvoices = async () => {

    try {

      setLoadingInvoices(true);

      const res = await fetch(
        `${BASE_URL}company/payment-invoices/${companyId}/${tx.payment_id}`
      );

      const data = await res.json();

      if (data?.success) {
        setInvoices(data.invoices || []);
      }

    } catch (err) {
      console.error(err);
    } finally {
      setLoadingInvoices(false);
    }
  };

  const handleToggle = async () => {

    const next = !open;
    setOpen(next);

    if (next && invoices.length === 0) {
      await fetchInvoices();
    }
  };

  return (
    <div className="rounded-2xl overflow-hidden">

      {/* HEADER */}

      <div
        onClick={handleToggle}
        className={`
        flex items-center justify-between
        p-3
        rounded-2xl
        transition
        cursor-pointer
        
        ${isRetailerMobile
          ? `bg-white/10
             backdrop-blur-2xl
             border border-black/30
             text-slate-800
             shadow-[0_20px_50px_rgba(31,38,135,0.25),0_8px_20px_rgba(0,0,0,0.15),inset_0_1px_1px_rgba(255,255,255,0.35)]
             hover:scale-[1.02]`
          : `bg-white border shadow-sm hover:bg-gray-50`
        }
      `}
      >

        <div className="flex items-center gap-3">

          {/* ICON */}

          <div
            className={`
            w-11 h-11
            flex items-center justify-center
            rounded-xl
            text-lg
            
            ${isRetailerMobile
              ? `bg-white/20 backdrop-blur border border-white/30`
              : `bg-gray-100`
            }
            `}
          >
            💰
          </div>

          {/* TEXT */}

          <div className="flex flex-col">

            <p className="text-sm font-semibold text-slate-900">
              Payment #{tx.payment_number}
            </p>

            <p className="text-xs text-slate-600">
              {tx.date}
            </p>

            <div className="flex gap-2 mt-1 flex-wrap">

              <span className="text-[11px] px-2 py-1 rounded-full bg-blue-100 text-blue-700">
                {tx.payment_mode_formatted}
              </span>

              {tx.unused_amount > 0 ? (
                <span className="text-[11px] px-2 py-1 rounded-full bg-yellow-100 text-yellow-700">
                  Unused ₹{tx.unused_amount}
                </span>
              ) : (
                <span className="text-[11px] px-2 py-1 rounded-full bg-green-100 text-green-700">
                  Fully Used
                </span>
              )}

            </div>

          </div>

        </div>

        <div className="flex flex-col items-end gap-2">

          <p className="text-lg font-bold text-green-600">
            ₹{tx.amount}
          </p>

          <div className="text-slate-500">

            {open ? (
              <ChevronUp className="w-5 h-5" />
            ) : (
              <ChevronDown className="w-5 h-5" />
            )}

          </div>

        </div>

      </div>


      {/* BODY */}

      {open && (

        <div
          className={`
          m-2
          p-4
          rounded-2xl
          
          ${isRetailerMobile
            ? `bg-white/10
               backdrop-blur-2xl
               border border-black/30
               shadow-[0_20px_50px_rgba(31,38,135,0.25),0_10px_25px_rgba(0,0,0,0.2)]`
            : `bg-white border shadow-sm`
          }
        `}
        >

          {loadingInvoices ? (

            <InvoiceSkeleton />

          ) : (

            <>
              <p className="text-xs font-semibold text-slate-600 mb-3">
                Invoice Details
              </p>

              {invoices.length === 0 ? (

                <div className="text-xs text-slate-500 bg-gray-50 rounded-xl p-3">
                  No invoices applied
                </div>

              ) : (

                <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">

                  {invoices.map((inv) => (
                    <div
                      key={inv.invoice_id}
                      className="flex justify-between items-center p-3 rounded-xl border bg-white/10 border-black/30 "
                    >

                      <div className="flex flex-col">

                        <span className="text-sm font-semibold text-slate-900">
                          {inv.invoice_number}
                        </span>

                        <span className="text-xs text-slate-600">
                          {inv.date}
                        </span>

                      </div>

                      <div className="text-right">

                        <p className="text-xs text-slate-500">
                          Applied
                        </p>

                        <p className="text-sm font-semibold text-green-600">
                          ₹{inv.amount_applied}
                        </p>

                      </div>

                    </div>
                  ))}

                </div>

              )}

            </>
          )}

        </div>

      )}

    </div>
  );
}


/* TRANSACTION SKELETON */

function TransactionSkeleton({ isRetailerMobile }) {

  return (
    <div
      className={`
      flex items-center justify-between
      p-3
      rounded-2xl
      animate-pulse
      
      ${isRetailerMobile
        ? "bg-white/10 backdrop-blur-2xl border border-black/30"
        : "bg-white border shadow-sm"
      }
      `}
    >

      <div className="flex items-center gap-3">

        <div className="w-11 h-11 rounded-xl bg-gray-300" />

        <div className="space-y-2">

          <div className="w-32 h-3 bg-gray-300 rounded" />

          <div className="w-20 h-2 bg-gray-200 rounded" />

        </div>

      </div>

      <div className="space-y-2">

        <div className="w-16 h-4 bg-gray-300 rounded" />

        <div className="w-5 h-5 bg-gray-200 rounded" />

      </div>

    </div>
  );
}


/* INVOICE SKELETON */

function InvoiceSkeleton() {

  return (
    <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 animate-pulse">

      {[1,2,3,4].map((i) => (

        <div
          key={i}
          className="p-3 rounded-xl border bg-gray-100"
        >

          <div className="h-3 w-24 bg-gray-300 rounded mb-2"></div>

          <div className="h-2 w-16 bg-gray-200 rounded mb-2"></div>

          <div className="h-3 w-12 bg-gray-300 rounded"></div>

        </div>

      ))}

    </div>
  );
}

export default RetailerTransactions;


// import React, { useEffect, useState } from "react";
// import { BASE_URL } from "../../../config/api";
// import { ChevronDown, ChevronUp } from "lucide-react";

// function RetailerTransactions() {

//   const [transactions, setTransactions] = useState([]);
//   const [loading, setLoading] = useState(true);

//   const authUser = JSON.parse(localStorage.getItem("authUserData"));
//   const companyId = authUser?.company_id;
//   const retailerId = authUser?.id;
//   const role = Number(authUser?.role);

//   const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

//   useEffect(() => {
//     const handleResize = () => setIsMobile(window.innerWidth < 768);

//     window.addEventListener("resize", handleResize);
//     return () => window.removeEventListener("resize", handleResize);
//   }, []);

//   const isRetailerMobile = role === 5 && isMobile;

//   const fetchTransactions = async () => {
//     try {

//       const res = await fetch(
//         `${BASE_URL}company/retailer-transactions/${companyId}/${retailerId}`
//       );

//       const data = await res.json();

//       if (data?.success) {
//         setTransactions(data.transactions || []);
//       }

//     } catch (err) {
//       console.error(err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchTransactions();
//   }, []);

//   return (
//     <div className="p-4">

//       <h2 className="text-xl font-semibold text-gray-800 mb-4">
//         Wallet Transactions
//       </h2>

//       {loading ? (
//         <p>Loading...</p>
//       ) : transactions.length === 0 ? (
//         <p>No transactions found</p>
//       ) : (

//         <div className="space-y-3">

//           {transactions.map((tx) => (
//             <TransactionRow
//               key={tx.payment_id}
//               tx={tx}
//               companyId={companyId}
//               isRetailerMobile={isRetailerMobile}
//             />
//           ))}

//         </div>

//       )}

//     </div>
//   );
// }

// function TransactionRow({ tx, companyId, isRetailerMobile }) {

//   const [open, setOpen] = useState(false);
//   const [invoices, setInvoices] = useState([]);
//   const [loadingInvoices, setLoadingInvoices] = useState(false);

//   const fetchInvoices = async () => {

//     try {

//       setLoadingInvoices(true);

//       const res = await fetch(
//         `${BASE_URL}company/payment-invoices/${companyId}/${tx.payment_id}`
//       );

//       const data = await res.json();

//       if (data?.success) {
//         setInvoices(data.invoices || []);
//       }

//     } catch (err) {
//       console.error(err);
//     } finally {
//       setLoadingInvoices(false);
//     }
//   };

//   const handleToggle = async () => {

//     const next = !open;
//     setOpen(next);

//     if (next && invoices.length === 0) {
//       await fetchInvoices();
//     }
//   };

//   return (
//     <div className="rounded-2xl overflow-hidden">

//       {/* CARD HEADER */}

//       <div
//         onClick={handleToggle}
//         className={`
//         flex items-center justify-between
//         p-3
//         rounded-2xl
//         transition
//         cursor-pointer
        
//         ${isRetailerMobile
//           ? `bg-white/10
//              backdrop-blur-2xl
//              border border-black/30
//              text-slate-800
//              shadow-[0_20px_50px_rgba(31,38,135,0.25),0_8px_20px_rgba(0,0,0,0.15),inset_0_1px_1px_rgba(255,255,255,0.35)]
//              hover:scale-[1.02]`
//           : `bg-white
//              border
//              shadow-sm
//              hover:bg-gray-50`
//         }
//       `}
//       >

//         {/* LEFT SIDE */}

//         <div className="flex items-center gap-3">

//           {/* ICON */}

//           <div
//             className={`
//             w-11 h-11
//             flex items-center justify-center
//             rounded-xl
//             text-lg
            
//             ${isRetailerMobile
//               ? `bg-white/20 backdrop-blur border border-white/30`
//               : `bg-gray-100`
//             }
//             `}
//           >
//             💰
//           </div>

//           {/* TEXT */}

//           <div className="flex flex-col">

//             <p className="text-sm font-semibold text-slate-900">
//               Payment #{tx.payment_number}
//             </p>

//             <p className="text-xs text-slate-600">
//               {tx.date}
//             </p>

//             <div className="flex gap-2 mt-1 flex-wrap">

//               <span className="text-[11px] px-2 py-1 rounded-full bg-blue-100 text-blue-700">
//                 {tx.payment_mode_formatted}
//               </span>

//               {tx.unused_amount > 0 ? (
//                 <span className="text-[11px] px-2 py-1 rounded-full bg-yellow-100 text-yellow-700">
//                   Unused ₹{tx.unused_amount}
//                 </span>
//               ) : (
//                 <span className="text-[11px] px-2 py-1 rounded-full bg-green-100 text-green-700">
//                   Fully Used
//                 </span>
//               )}

//             </div>

//           </div>

//         </div>


//         {/* RIGHT SIDE */}

//         <div className="flex flex-col items-end gap-2">

//           <p className="text-lg font-bold text-green-600">
//             ₹{tx.amount}
//           </p>

//           <div className="text-slate-500">

//             {open ? (
//               <ChevronUp className="w-5 h-5" />
//             ) : (
//               <ChevronDown className="w-5 h-5" />
//             )}

//           </div>

//         </div>

//       </div>


//       {/* BODY */}

//       {open && (

//         <div
//           className={`
//           m-2
//           p-4
//           rounded-2xl
          
//           ${isRetailerMobile
//             ? `bg-white/10
//                backdrop-blur-2xl
//                border border-black/30
//                shadow-[0_20px_50px_rgba(31,38,135,0.25),0_10px_25px_rgba(0,0,0,0.2)]`
//             : `bg-white
//                border
//                shadow-sm`
//           }
//         `}
//         >

//           {loadingInvoices ? (
//             <p className="text-sm text-slate-700">
//               Loading invoices...
//             </p>
//           ) : (
//             <>

//               <p className="text-xs font-semibold text-slate-600 mb-3">
//                 Invoice Details
//               </p>

//               {invoices.length === 0 ? (

//                 <div className="text-xs text-slate-500 bg-gray-50 rounded-xl p-3">
//                   No invoices applied
//                 </div>

//               ) : (

//                 <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">

//                   {invoices.map((inv) => (
//                     <div
//                       key={inv.invoice_id}
//                       className="flex justify-between items-center p-3 rounded-xl border bg-gray-50"
//                     >

//                       <div className="flex flex-col">

//                         <span className="text-sm font-semibold text-slate-900">
//                           {inv.invoice_number}
//                         </span>

//                         <span className="text-xs text-slate-600">
//                           {inv.date}
//                         </span>

//                       </div>

//                       <div className="text-right">

//                         <p className="text-xs text-slate-500">
//                           Applied
//                         </p>

//                         <p className="text-sm font-semibold text-green-600">
//                           ₹{inv.amount_applied}
//                         </p>

//                       </div>

//                     </div>
//                   ))}

//                 </div>

//               )}

//             </>
//           )}

//         </div>

//       )}

//     </div>
//   );
// }

// export default RetailerTransactions;

// import React, { useEffect, useState } from "react";
// import { BASE_URL } from "../../../config/api";
// import { ChevronDown, ChevronUp } from "lucide-react";

// function RetailerTransactions() {

//   const [transactions, setTransactions] = useState([]);
//   const [loading, setLoading] = useState(true);

//   const authUser = JSON.parse(localStorage.getItem("authUserData"));
//   const companyId = authUser?.company_id;
//   const retailerId = authUser?.id;

//   const fetchTransactions = async () => {
//     try {
//       const res = await fetch(
//         `${BASE_URL}company/retailer-transactions/${companyId}/${retailerId}`
//       );

//       const data = await res.json();

//       if (data?.success) {
//         setTransactions(data.transactions || []);
//       }

//     } catch (err) {
//       console.error(err);
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchTransactions();
//   }, []);

//   return (
//     <div className="p-2 space-y-3">

//       <h2 className="text-xl font-bold">Wallet Transactions</h2>

//       {loading ? (
//         <p>Loading...</p>
//       ) : transactions.length === 0 ? (
//         <p>No transactions found</p>
//       ) : (
//         transactions.map((tx) => (
//           <TransactionRow
//             key={tx.payment_id}
//             tx={tx}
//             companyId={companyId}
//           />
//         ))
//       )}

//     </div>
//   );
// }

// function TransactionRow({ tx, companyId }) {

//   const [open, setOpen] = useState(false);
//   const [invoices, setInvoices] = useState([]);
//   const [loadingInvoices, setLoadingInvoices] = useState(false);

//   const fetchInvoices = async () => {
//     try {
//       setLoadingInvoices(true);

//       const res = await fetch(
//         `${BASE_URL}company/payment-invoices/${companyId}/${tx.payment_id}`
//       );

//       const data = await res.json();

//       if (data?.success) {
//         setInvoices(data.invoices || []);
//       }

//     } catch (err) {
//       console.error(err);
//     } finally {
//       setLoadingInvoices(false);
//     }
//   };

//   const handleToggle = async () => {
//     const next = !open;
//     setOpen(next);

//     if (next && invoices.length === 0) {
//       await fetchInvoices();
//     }
//   };

//   const hasUnused = tx.unused_amount > 0;

//   return (
//     <div className="rounded-2xl overflow-hidden">

//       {/* CARD HEADER */}
//      <div
//         onClick={handleToggle}
//         className="
//         flex items-center justify-between
//         p-3
//         rounded-2xl
//         bg-white/10
//         backdrop-blur-2xl
//         border border-black/30
//         text-slate-800
//         shadow-[0_20px_50px_rgba(31,38,135,0.25),0_8px_20px_rgba(0,0,0,0.15),inset_0_1px_1px_rgba(255,255,255,0.35)]
//         hover:scale-[1.02]
//         transition
//         cursor-pointer
//         "
//       >

//           {/* LEFT SIDE */}
//           <div className="flex items-center gap-3">

//             {/* ICON */}
//             <div
//               className="
//               w-11 h-11
//               flex items-center justify-center
//               rounded-xl
//               bg-white/20
//               backdrop-blur
//               border border-white/30
//               shadow-inner
//               text-lg
//               "
//             >
//               💰
//             </div>

//             {/* TEXT */}
//             <div className="flex flex-col">

//               <p className="text-sm font-semibold text-slate-900">
//                 Payment #{tx.payment_number}
//               </p>

//               <p className="text-xs text-slate-600">
//                 {tx.date}
//               </p>

//               {/* STATUS */}
//               <div className="flex gap-2 mt-1 flex-wrap">

//                 <span className="text-[11px] px-2 py-1 rounded-full bg-blue-100 text-blue-700">
//                   {tx.payment_mode_formatted}
//                 </span>

//                 {tx.unused_amount > 0 ? (
//                   <span className="text-[11px] px-2 py-1 rounded-full bg-yellow-100 text-yellow-700">
//                     Unused ₹{tx.unused_amount}
//                   </span>
//                 ) : (
//                   <span className="text-[11px] px-2 py-1 rounded-full bg-green-100 text-green-700">
//                     Fully Used
//                   </span>
//                 )}

//               </div>

//             </div>
//           </div>


//           {/* RIGHT SIDE */}
//           <div className="flex flex-col items-end gap-2">
//             <p className="text-lg font-bold text-green-600">
//               ₹{tx.amount}
//             </p>

//             <div className="text-slate-500">
//               {open ? (
//                 <ChevronUp className="w-5 h-5" />
//               ) : (
//                 <ChevronDown className="w-5 h-5" />
//               )}
//             </div>
//           </div>

//         </div>

//       {/* BODY */}
//       {open && (
//         <div
//           className="
//           m-2
//           p-4
//           rounded-2xl
//           bg-white/10
//           backdrop-blur-2xl
//           border border-black/30
//           shadow-[0_20px_50px_rgba(31,38,135,0.25),0_10px_25px_rgba(0,0,0,0.2)]
//           "
//         >

//           {loadingInvoices ? (
//             <p className="text-sm text-slate-700">
//               Loading invoices...
//             </p>
//           ) : (
//             <>
//               <p className="text-xs font-semibold text-slate-600 mb-3">
//                 Invoice Details
//               </p>

//               {invoices.length === 0 ? (

//                 <div className="text-xs text-slate-500 bg-white/20 rounded-xl p-3">
//                   No invoices applied
//                 </div>

//               ) : (

//                 <div className="grid grid-cols-1 gap-3">

//                   {invoices.map((inv) => (
//                     <div
//                       key={inv.invoice_id}
//                       className="
//                       flex justify-between items-center
//                       p-3
//                       rounded-xl
//                       bg-white/20
//                       backdrop-blur
//                       border border-black/20
//                       shadow-sm
//                       "
//                     >

//                       {/* LEFT */}
//                       <div className="flex flex-col">

//                         <span className="text-sm font-semibold text-slate-900">
//                           {inv.invoice_number}
//                         </span>

//                         <span className="text-xs text-slate-600">
//                           {inv.date}
//                         </span>

//                       </div>

//                       {/* RIGHT */}
//                       <div className="text-right">

//                         <p className="text-xs text-slate-500">
//                           Applied
//                         </p>

//                         <p className="text-sm font-semibold text-green-600">
//                           ₹{inv.amount_applied}
//                         </p>

//                       </div>

//                     </div>
//                   ))}

//                 </div>

//               )}
//             </>
//           )}

//         </div>
//       )}

//     </div>
//   );
// }

// export default RetailerTransactions;